import { Download } from "lucide-react";
import { useId, useRef, useState } from "react";

import { Button } from "@wellcheck/components/ui/button";

import {
  langLabel,
  parseRespondentsCsv,
  type ParseError,
  type Respondent,
} from "@/features/upload/data/respondentsCsv";

export interface Step1RespondentsUploadProps {
  defaultValue?: Respondent[];
  onParsed: (rows: Respondent[]) => void;
  /**
   * 選択されたファイルの生テキスト (UTF-8) を親に通知する。
   * サーバ側 (backend/officeinput) が CSV を権威的に再パースするため (ADR-0025)、送信時に
   * このテキストをそのまま送る。パースエラーの有無に関わらず選択されるたびに呼ばれる。
   */
  onFileText?: (csvText: string) => void;
  /**
   * サーバ側バリデーション失敗時の行単位エラー (ADR-0025 §5)。
   * クライアント側 preview のエラーとは別に追加表示する (再送信を促す)。
   */
  submitErrors?: ParseError[];
}

interface FileState {
  fileName: string;
  rows: Respondent[];
  errors: ParseError[];
}

const SAMPLE_CSV_PATH = "/samples/名簿サンプル.csv";

/**
 * Step1RespondentsUpload — 名簿 CSV を選択しブラウザ内で先読み確認するステップ。
 *
 *  - CSV のみ受け付ける (Excel ユーザーには「名前を付けて保存 → CSV UTF-8」を案内)
 *  - パース成功は親に通知し、Wizard 側の「次へ」を enable
 *  - パースエラーは画面で行ごとに表示し、再選択を促す
 */
export function Step1RespondentsUpload({
  defaultValue,
  onParsed,
  onFileText,
  submitErrors,
}: Step1RespondentsUploadProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<FileState | null>(
    defaultValue && defaultValue.length > 0
      ? { fileName: "(前回選択したファイル)", rows: defaultValue, errors: [] }
      : null,
  );
  const [isParsing, setIsParsing] = useState(false);

  const handleFile = async (file: File) => {
    setIsParsing(true);
    try {
      const { rows, errors } = await parseRespondentsCsv(file);
      setState({ fileName: file.name, rows, errors });
      if (errors.length === 0 && rows.length > 0) {
        onParsed(rows);
      } else {
        onParsed([]);
      }
      onFileText?.(await file.text());
    } finally {
      setIsParsing(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void handleFile(file);
  };

  const reset = () => {
    setState(null);
    onParsed([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-md border border-dashed border-border bg-muted/30 px-6 py-8 text-center">
        <div className="space-y-1">
          <p className="text-sm font-medium">名簿 CSV を選択してください</p>
          <p className="text-xs text-muted-foreground">
            必須列: <span className="font-medium text-foreground">氏名</span> /{" "}
            <span className="font-medium text-foreground">社員番号</span> /{" "}
            <span className="font-medium text-foreground">性別</span> /{" "}
            <span className="font-medium text-foreground">メールアドレス</span> /{" "}
            <span className="font-medium text-foreground">部署名</span> (部署名の値は空欄可)
            <br />
            任意列: <span className="font-medium text-foreground">連絡方法</span>{" "}
            (現在は「メール」のみ指定できます。空欄もメールとして扱います) /{" "}
            <span className="font-medium text-foreground">言語</span> (日本語 / 英語 / 中国語 /
            ベトナム語 / ポルトガル語)
            <br />
            言語は<span className="font-medium text-foreground">結果帳票 (PDF) を出力する言語</span>
            です。回答画面の言語はご本人が回答時に選ぶため、この列では変わりません。空欄の方は日本語で出力します。
            <br />
            Excel から書き出す場合は「名前を付けて保存」→「CSV UTF-8 (.csv)」を選んでください。
          </p>
        </div>
        <div className="flex justify-center">
          <Button asChild variant="outline">
            <a href={SAMPLE_CSV_PATH} download>
              <Download />
              サンプル CSV を DL
            </a>
          </Button>
        </div>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={onChange}
        />
        <div className="flex justify-center pt-2">
          <Button asChild size="lg">
            <label htmlFor={inputId} className="cursor-pointer">
              {state ? "ファイルを選び直す" : "ファイルを選択"}
            </label>
          </Button>
        </div>
      </div>

      {isParsing && <p className="text-sm text-muted-foreground">読み込み中...</p>}

      {submitErrors && submitErrors.length > 0 && (
        <ErrorBox
          title="送信時にサーバでエラーが検出されました"
          messages={submitErrors.map((e) =>
            e.kind === "row" ? `${e.rowNumber} 行目: ${e.message}` : e.message,
          )}
        />
      )}

      {state && !isParsing && <ParseSummary state={state} onReset={reset} />}
    </div>
  );
}

interface ParseSummaryProps {
  state: FileState;
  onReset: () => void;
}

function ParseSummary({ state, onReset }: ParseSummaryProps) {
  const hasFileError = state.errors.some((e) => e.kind === "file");
  const rowErrors = state.errors.filter(
    (e): e is Extract<ParseError, { kind: "row" }> => e.kind === "row",
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm">
          <span className="font-medium">{state.fileName}</span> を読み込みました
        </p>
        <Button variant="link" size="sm" onClick={onReset} className="px-0 text-muted-foreground">
          選択を解除
        </Button>
      </div>

      {hasFileError ? (
        <ErrorBox
          title="ファイル形式が正しくありません"
          messages={state.errors.filter((e) => e.kind === "file").map((e) => e.message)}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm">
          <Metric label="取り込み可能な行" value={`${state.rows.length} 件`} tone="ok" />
          <Metric
            label="エラー行"
            value={`${rowErrors.length} 件`}
            tone={rowErrors.length > 0 ? "warn" : "muted"}
          />
        </div>
      )}

      {rowErrors.length > 0 && (
        <ErrorBox
          title={`エラーがある行 (${rowErrors.length} 件)`}
          messages={rowErrors.map((e) => `${e.rowNumber} 行目: ${e.message}`)}
        />
      )}

      {!hasFileError && state.rows.length > 0 && <PreviewTable rows={state.rows} />}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "muted";
}) {
  const valueClass =
    tone === "ok"
      ? "text-foreground"
      : tone === "warn"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-base font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

function ErrorBox({ title, messages }: { title: string; messages: string[] }) {
  return (
    <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
      <p className="text-sm font-medium text-destructive">{title}</p>
      <ul className="space-y-1 text-sm text-destructive">
        {messages.map((m, i) => (
          <li key={i}>・{m}</li>
        ))}
      </ul>
    </div>
  );
}

function PreviewTable({ rows }: { rows: Respondent[] }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{rows.length} 件のプレビュー</p>
      <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">氏名</th>
              <th className="px-3 py-2 text-left font-medium">社員番号</th>
              <th className="px-3 py-2 text-left font-medium">性別</th>
              <th className="px-3 py-2 text-left font-medium">メールアドレス</th>
              <th className="px-3 py-2 text-left font-medium">部署名</th>
              <th className="px-3 py-2 text-left font-medium">言語</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2">{r.fullName}</td>
                <td className="px-3 py-2">{r.employeeNumber}</td>
                <td className="px-3 py-2">{r.gender}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.email}</td>
                <td className="px-3 py-2">{r.department}</td>
                <td className="px-3 py-2">{langLabel(r.lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
